/**
 * Settings > Checker > Rules (W2-13 settings-keymap; DESIGN.md §4.20, §5.10, §7.6).
 *
 * Every checker rule (from the problems service, i.e. the `*.rules.ts` files) grouped by category — Phira
 * compatibility, gameplay, style — with its id, severity (icon and word) and level; a switch turns it on or off
 * (settings.checker.disabledRules), each category can be turned on or off at once, and one button turns every rule
 * back on. The thresholds (RPE auto-check parameters) are ordinary rows above this block.
 */

import { useEffect, useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { CheckCategory } from '../../core/check/types';
import type { MessageRef } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { Badge, Button, EmptyState, Switch } from '../../ui';
import { useSettingsValue, type SettingsText } from './hooks';
import { matchesTerms, searchTerms } from './keymapModel';
import { m } from './settings.messages';
import s from './Settings.module.css';

type RuleInfo = ReturnType<AppServices['problems']['rules']>[number];

const CATEGORY_ORDER: readonly CheckCategory[] = ['phira', 'gameplay', 'style'];

const CATEGORY_TITLE: Readonly<Record<CheckCategory, MessageKey>> = {
  phira: m.ruleCatPhira,
  gameplay: m.ruleCatGameplay,
  style: m.ruleCatStyle,
};

const CATEGORY_DESC: Readonly<Record<CheckCategory, MessageKey>> = {
  phira: m.ruleCatPhiraDesc,
  gameplay: m.ruleCatGameplayDesc,
  style: m.ruleCatStyleDesc,
};

function ruleTexts(tr: (ref: MessageRef) => string, r: RuleInfo): string[] {
  return [tr(r.title), r.id, r.severity, r.category];
}

/** Number of rules the Settings search finds (for the page's result counts). */
export function ruleSearchMatches(
  services: Pick<AppServices, 'problems'>,
  tr: (ref: MessageRef) => string,
  query: string,
): number {
  const terms = searchTerms(query);
  if (terms.length === 0) return 0;
  return services.problems.rules().filter((r) => matchesTerms(terms, ruleTexts(tr, r))).length;
}

export interface CheckerSectionProps {
  readonly services: AppServices;
  readonly text: SettingsText;
  /** The Settings page search. */
  readonly query?: string;
}

export function CheckerSection(props: CheckerSectionProps) {
  const { services, text } = props;
  const { t, tr } = text;
  const settings = useSettingsValue(services);
  const [rules, setRules] = useState(() => services.problems.rules());
  useEffect(() => {
    const problems = services.problems;
    return problems.subscribe(() => {
      setRules(problems.rules());
    });
  }, [services.problems]);

  const disabled = new Set(settings.checker.disabledRules);
  const guided = settings.general.level === 'guided';
  const query = (props.query ?? '').trim();
  const terms = searchTerms(query);
  const shown = rules.filter((r) => matchesTerms(terms, ruleTexts(tr, r)));

  const setDisabled = (next: ReadonlySet<string>): void => {
    services.settings.set('checker', 'disabledRules', [...next].sort());
  };
  const toggle = (id: string, on: boolean): void => {
    const next = new Set(disabled);
    if (on) next.delete(id);
    else next.add(id);
    setDisabled(next);
  };
  const setCategory = (cat: CheckCategory, on: boolean): void => {
    const next = new Set(disabled);
    for (const r of rules) {
      if (r.category !== cat) continue;
      if (on) next.delete(r.id);
      else next.add(r.id);
    }
    setDisabled(next);
  };

  return (
    <div className={s.rules}>
      <div className={s.blockHeader}>
        <div>
          <h3 className={s.blockTitle}>{t(m.rulesTitle)}</h3>
          <p className={s.blockDesc}>{t(m.rulesDesc)}</p>
        </div>
        <Button
          icon="ListRestart"
          disabledReason={disabled.size === 0 ? { key: m.rulesAllOn } : null}
          onClick={() => {
            services.settings.reset('checker.disabledRules');
          }}
        >
          {t(m.rulesEnableAll)}
        </Button>
      </div>
      {rules.length === 0 ? (
        <EmptyState icon="ListChecks" title={{ key: m.rulesNone }} body={{ key: m.rulesNoneBody }} />
      ) : shown.length === 0 ? (
        <p className={s.kmEmpty}>{t(m.rulesNoMatch, { query })}</p>
      ) : (
        CATEGORY_ORDER.map((cat) => {
          const all = rules.filter((r) => r.category === cat);
          const list = shown.filter((r) => r.category === cat);
          if (list.length === 0) return null;
          const on = all.filter((r) => !disabled.has(r.id)).length;
          return (
            <section key={cat} className={s.ruleGroup} aria-labelledby={`rules-${cat}`}>
              <div className={s.ruleGroupHeader}>
                <div>
                  <h4 id={`rules-${cat}`} className={s.subTitle}>
                    {t(CATEGORY_TITLE[cat])}
                  </h4>
                  <p className={s.blockDesc}>
                    {t(CATEGORY_DESC[cat])} {t(m.rulesOnCount, { on, total: all.length })}
                  </p>
                </div>
                <div className={s.inline}>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabledReason={on === all.length ? { key: m.rulesAllOn } : null}
                    onClick={() => {
                      setCategory(cat, true);
                    }}
                  >
                    {t(m.rulesCategoryOn)}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabledReason={on === 0 ? { key: m.rulesAllOff } : null}
                    onClick={() => {
                      setCategory(cat, false);
                    }}
                  >
                    {t(m.rulesCategoryOff)}
                  </Button>
                </div>
              </div>
              <ul className={s.ruleList}>
                {list.map((r) => (
                  <li key={r.id} className={s.ruleItem} data-off={disabled.has(r.id) ? 'true' : undefined}>
                    <Switch
                      label={tr(r.title)}
                      checked={!disabled.has(r.id)}
                      onChange={(v) => {
                        toggle(r.id, v);
                      }}
                    />
                    <span className={s.kmId}>{r.id}</span>
                    <Badge kind="problem" severity={r.severity} />
                    {r.level === 'full' && guided && <Badge kind="full" />}
                  </li>
                ))}
              </ul>
            </section>
          );
        })
      )}
    </div>
  );
}
